import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  TextField,
  DialogContent,
  FormControl,
  Chip,
} from '@mui/material';
import debounce from 'lodash/debounce';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';
import { TagContainer, ChipsContainer } from 'SandboxModule/styles';
import { updateSandbox } from 'thunk';
import { useAppDispatch } from 'redux/hooks';
import { useEffect } from 'react';
import EllipsisText from 'components/EllipsisText';
import { STAND_BY_TAG, chipWidth } from '@constants';
import { RealmTypes } from 'RealmModule/constants/realm';
import { useSandboxModalFields } from 'SandboxModule/hooks/useSandboxModalFields';

export type EditRealmModalProps = {
  onClose: VoidFunction;
  realmId: string;
  onEditRealmSuccess?: VoidFunction;
  realmType: RealmTypes;
};

export const EditRealmModal = ({
  onClose,
  realmId,
  onEditRealmSuccess,
  realmType,
}: EditRealmModalProps) => {
  const { getSandbox } = useSandboxes();
  const sandbox = getSandbox(realmId);
  const dispatch = useAppDispatch();

  const { addTag, removeTag, inputRef, tagsList, tagError, setTagsList, teamNameRef, creatorRef } =
    useSandboxModalFields();

  // Updating draft local tags list
  useEffect(() => {
    if (sandbox) {
      setTagsList(sandbox.tags);
    }
  }, [sandbox, sandbox?.tags, setTagsList]);

  const handleSubmitEditSandboxForm = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const params = {
      realmId,
      teamName: teamNameRef.current.value,
      creator: creatorRef.current?.value || '',
      tags: tagsList,
      onSuccess: ({ response }: any) => {
        onClose();
        onEditRealmSuccess?.();
      },
    };
    dispatch(updateSandbox(params));
  };
  return (
    <Dialog maxWidth={'xs'} fullWidth open onClose={onClose}>
      <DialogTitle id="form-dialog-title">
        Edit {realmType}: {sandbox?.['team_name']}
      </DialogTitle>
      <DialogContent>
        <form>
          <TextField
            required
            inputRef={teamNameRef}
            defaultValue={sandbox?.['team_name']}
            label="Team Name"
            name="teamName"
            type="text"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            inputRef={creatorRef}
            defaultValue={sandbox?.['created_by']}
            label="Creator"
            name="creator"
            type="text"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            style={{ marginTop: '1rem' }}
          />

          <FormControl component="fieldset" fullWidth>
            <Box component="h3">Edit tags</Box>
            <TagContainer>
              <div>
                <TextField
                  placeholder="Enter custom tag"
                  inputRef={inputRef}
                  onKeyDown={addTag}
                  helperText={tagError}
                  error={!!tagError}
                />
                <ChipsContainer>
                  {tagsList?.map((tag: string) => (
                    <EllipsisText key={tag} text={tag} width={chipWidth}>
                      <Chip
                        className="chip"
                        color="primary"
                        label={tag}
                        onDelete={tag === STAND_BY_TAG ? undefined : () => removeTag(tag)}
                        style={{ maxWidth: `calc(${chipWidth} - 10%)` }}
                      />
                    </EllipsisText>
                  ))}
                </ChipsContainer>
              </div>
            </TagContainer>
          </FormControl>

          <DialogActions>
            <Button color="primary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!!tagError} color="primary" onClick={handleSubmitEditSandboxForm}>
              Save
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};
